import { useParams, useSearchParams } from "react-router-dom";
import { mapHotelsSearchToSubmitObject } from "../utils";
import { useMemo, useLayoutEffect, useReducer } from "react";
import { useLazySearchRoomsQuery } from "src/store/apis/Hotels";
import { Hotel } from "src/store/apis/Hotels";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/store";
import { cancelTimer, resetTimerState, setMaxTimeThunk, setSearchId, startTimer } from "src/store/slices/timer";
import dayjs from "dayjs";
import { simpleHash } from "src/utility";

/* Status discriminated union: enforces valid state combinations */
type Status = "idle" | "fetching-first" | "has-results-fetching-more" | "complete" | "error";

interface HotelsSearchState {
  status: Status;
  accumulatedHotels: Hotel[];
  pointsFactor: number;
  sessionId: string;
  error: string | null;
}

type HotelsSearchAction =
  | { type: "START_FETCH_FIRST" }
  | { type: "RECEIVE_PAGE"; payload: { hotels: Hotel[]; isLast: boolean; pointsFactor: number; sessionId: string } }
  | { type: "FETCH_ERROR"; payload: string }
  | { type: "COMPLETE" };

/**
 * Reducer for hotels search polling lifecycle.
 * Enforces valid state transitions via discriminated union.
 */
function hotelsSearchReducer(state: HotelsSearchState, action: HotelsSearchAction): HotelsSearchState {
  switch (action.type) {
    case "START_FETCH_FIRST":
      return {
        status: "fetching-first",
        accumulatedHotels: [],
        pointsFactor: 1,
        sessionId: "",
        error: null,
      };

    case "RECEIVE_PAGE": {
      const { hotels, isLast, pointsFactor, sessionId } = action.payload;

      if (isLast) {
        return {
          ...state,
          status: "complete",
          accumulatedHotels: [...state.accumulatedHotels, ...hotels],
          pointsFactor,
          sessionId: sessionId || state.sessionId,
          error: null,
        };
      }

      const hasResults = state.accumulatedHotels.length > 0 || hotels.length > 0;
      const nextStatus: Status = hasResults ? "has-results-fetching-more" : "fetching-first";

      return {
        status: nextStatus,
        accumulatedHotels: [...state.accumulatedHotels, ...hotels],
        pointsFactor,
        sessionId: sessionId || state.sessionId,
        error: null,
      };
    }

    case "FETCH_ERROR": {
      return {
        ...state,
        status: "complete",
        error: action.payload,
      };
    }

    case "COMPLETE":
      return {
        ...state,
        status: "complete",
      };

    default:
      return state;
  }
}

const initialState: HotelsSearchState = {
  status: "idle",
  accumulatedHotels: [],
  pointsFactor: 1,
  sessionId: "",
  error: null,
};

export function useHotelsSearchObject() {
  const { searchId } = useParams();
  const searchFormInputJSON = localStorage.getItem(searchId ?? "");
  const notFound = searchId !== undefined && searchFormInputJSON === null;
  const searchFormInputObject = useMemo(() => (searchFormInputJSON ? JSON.parse(searchFormInputJSON) : null), [searchFormInputJSON]);
  return { searchObject: searchFormInputObject, notFound };
}

export default function useHotelsSearch() {
  const { searchId } = useParams();
  const [searchParams] = useSearchParams();
  const time = searchParams.get("t") ?? "";
  const { searchObject: searchFormInputObject, notFound } = useHotelsSearchObject();
  const baseRequest = useMemo(() => mapHotelsSearchToSubmitObject(searchFormInputObject), [searchFormInputObject]);

  const [triggerSearch] = useLazySearchRoomsQuery();
  const [state, reducerDispatch] = useReducer(hotelsSearchReducer, initialState);
  const dispatch = useDispatch<AppDispatch>();
  const searchIdStore = useSelector((state: RootState) => state.timer.searchId);

  // Derived values for convenience
  const hasFirstResults = state.accumulatedHotels.length > 0;
  const isFetching = state.status === "fetching-first" || state.status === "has-results-fetching-more";
  const isPollingDone = state.status === "complete";

  useLayoutEffect(() => {
    if (!baseRequest) return;
    let cancelled = false;
    const sleep = () => new Promise<void>((res) => setTimeout(res, 2000));

    const runPolling = async () => {
      dispatch(cancelTimer());
      reducerDispatch({ type: "START_FETCH_FIRST" });
      let offset = 0;

      while (!cancelled) {
        try {
          const response = await triggerSearch({ request: { ...baseRequest, offset }, cacheBuster: time }, true);
          if (cancelled) break;

          if (!response.data && offset === 0) {
            reducerDispatch({ type: "COMPLETE" });
            return;
          }

          if (!response.data) {
            offset++;
            await sleep();
            continue;
          }

          reducerDispatch({
            type: "RECEIVE_PAGE",
            payload: {
              hotels: response.data.hotels,
              isLast: response.data.isLastPolling,
              pointsFactor: response.data.pointsFactor,
              sessionId: response.data.session?.id ?? "",
            },
          });

          if (offset === 0 && response.data.session) {
            const { hash } = simpleHash(JSON.stringify(baseRequest), 6, time);
            if (searchIdStore !== hash) {
              dispatch(resetTimerState());
              const interval = dayjs(response.data.session.expiresAt).diff(response.data.session.startedAt, "millisecond");
              dispatch(setMaxTimeThunk(interval));
              dispatch(startTimer());
              dispatch(setSearchId(hash));
            }
          }

          if (response.data.isLastPolling) return;
        } catch (err) {
          if (cancelled) break;

          const errorMsg = err instanceof Error ? err.message : "Unknown error";
          reducerDispatch({ type: "FETCH_ERROR", payload: errorMsg });

          if (offset === 0) {
            reducerDispatch({ type: "COMPLETE" });
            return;
          }

          offset++;
          await sleep();
          continue;
        }

        offset++;
        await sleep();
      }
    };

    runPolling();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseRequest, time, searchId, dispatch]); // re-runs only when a new search is submitted

  return {
    searchObject: searchFormInputObject,
    notFound,
    hotels: state.accumulatedHotels,
    isPollingDone,
    isFetching,
    hasFirstResults,
    pointsFactor: state.pointsFactor,
    sessionId: state.sessionId,
    error: state.error,
  };
}
